import {
  ALWAYS,
  type ConditionRule,
  PERMISSION_KEYS,
  type PermissionKey,
  type Role,
} from '@servicetim/shared';
import { useGroups } from '@servicetim/ui/api';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { Plus, Trash2 } from 'lucide-react';
import type { FieldCatalog } from '../../projects/catalog/field-catalog';
import { FieldConditionRow } from '../../projects/conditions/field-condition-row';
import { removeAt, replaceAt } from '../../projects/lib/list-utils';
import { SimpleSelect } from '../../projects/ui/simple-select';

export interface ConditionBuilderProps {
  value: ConditionRule;
  onChange: (next: ConditionRule) => void;
  catalog: FieldCatalog;
  roles: readonly Role[];
  idPrefix: string;
  depth?: number;
}

type GroupKind = 'all' | 'any' | 'not';
type LeafType = 'has_permission' | 'user_is' | 'user_in_role' | 'user_in_group' | 'field_matches';

const GROUP_OPTIONS = [
  { value: 'all', label: 'All of' },
  { value: 'any', label: 'Any of' },
  { value: 'not', label: 'Not' },
];
const WHO_OPTIONS = [
  { value: 'assignee', label: 'the assignee' },
  { value: 'reporter', label: 'the reporter' },
  { value: 'project_lead', label: 'the project lead' },
];
const LEAF_LABELS: Record<LeafType, string> = {
  has_permission: 'User has permission',
  user_is: 'User is…',
  user_in_role: 'User holds project role',
  user_in_group: 'User is in group',
  field_matches: 'Field matches',
};

const groupKind = (rule: ConditionRule): GroupKind | null =>
  'all' in rule ? 'all' : 'any' in rule ? 'any' : 'not' in rule ? 'not' : null;

const children = (rule: ConditionRule): ConditionRule[] =>
  'all' in rule ? rule.all : 'any' in rule ? rule.any : 'not' in rule ? [rule.not] : [];

const makeGroup = (kind: GroupKind, items: ConditionRule[]): ConditionRule =>
  kind === 'all' ? { all: items } : kind === 'any' ? { any: items } : { not: items[0] ?? ALWAYS };

export function newLeaf(
  type: LeafType,
  catalog: FieldCatalog,
  roles: readonly Role[],
): ConditionRule {
  switch (type) {
    case 'has_permission':
      return { type, permission: 'ticket.transition' };
    case 'user_is':
      return { type, who: 'assignee' };
    case 'user_in_role':
      return { type, roleId: roles[0]?.id ?? '' };
    case 'user_in_group':
      return { type, groupId: '' };
    case 'field_matches':
      return { type, field: catalog.fields[0]?.key ?? 'status_id', op: 'eq' };
  }
}

/**
 * Recursive editor for the closed `ConditionRule` grammar: all / any / not groups and the five
 * leaf types. Values round-trip unchanged; the output is the JSON the API stores.
 */
export function ConditionBuilder({
  value,
  onChange,
  catalog,
  roles,
  idPrefix,
  depth = 0,
}: ConditionBuilderProps) {
  const kind = groupKind(value);
  if (kind === null) {
    return (
      <LeafEditor
        value={value}
        onChange={onChange}
        catalog={catalog}
        roles={roles}
        idPrefix={idPrefix}
      />
    );
  }
  const items = children(value);
  const setItems = (next: ConditionRule[]) => onChange(makeGroup(kind, next));
  const addMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={kind === 'not' && items.length >= 1 && !isAlways(items[0])}
        >
          <Plus />
          Add condition
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Condition</DropdownMenuLabel>
        {(Object.keys(LEAF_LABELS) as LeafType[]).map((type) => (
          <DropdownMenuItem key={type} onSelect={() => append(newLeaf(type, catalog, roles))}>
            {LEAF_LABELS[type]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Group</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => append({ all: [] })}>All of…</DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => append({ any: [newLeaf('has_permission', catalog, roles)] })}
        >
          Any of…
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => append({ not: newLeaf('user_is', catalog, roles) })}>
          Not…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
  function append(rule: ConditionRule) {
    if (kind === 'not') {
      setItems([rule]);
    } else {
      setItems([...items, rule]);
    }
  }

  return (
    <div
      className={cn(
        'space-y-2 rounded-md border p-3',
        depth === 0 ? 'border-border bg-surface-raised' : 'border-border bg-surface-sunken',
      )}
      data-slot="condition-group"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SimpleSelect
          aria-label="Group operator"
          size="sm"
          className="w-28"
          value={kind}
          options={GROUP_OPTIONS}
          onChange={(next) => {
            if (!next || next === kind) return;
            const nextKind = next as GroupKind;
            if (nextKind === 'not') onChange({ not: items[0] ?? ALWAYS });
            else if (kind === 'not') onChange(makeGroup(nextKind, isAlways(items[0]) ? [] : items));
            else onChange(makeGroup(nextKind, items));
          }}
        />
        <span className="text-fg-muted text-xs">
          {kind === 'all'
            ? items.length === 0
              ? 'these conditions (none yet: always allowed)'
              : 'these conditions must all hold'
            : kind === 'any'
              ? 'at least one must hold'
              : 'the condition below must not hold'}
        </span>
      </div>
      {items.length === 0 ? null : (
        <ul className="space-y-2">
          {items.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; their position is the key
            <li key={`${idPrefix}-${index}-${leafKey(item)}`} className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <ConditionBuilder
                  value={item}
                  onChange={(next) => setItems(replaceAt(items, index, next))}
                  catalog={catalog}
                  roles={roles}
                  idPrefix={`${idPrefix}-${index}`}
                  depth={depth + 1}
                />
              </div>
              {groupKind(item) !== null ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove group"
                  onClick={() => setItems(kind === 'not' ? [] : removeAt(items, index))}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {addMenu}
    </div>
  );
}

const isAlways = (rule: ConditionRule | undefined): boolean =>
  rule !== undefined && 'all' in rule && rule.all.length === 0;

const leafKey = (rule: ConditionRule): string =>
  'type' in rule ? rule.type : 'all' in rule ? 'all' : 'any' in rule ? 'any' : 'not';

function LeafEditor({
  value,
  onChange,
  catalog,
  roles,
  idPrefix,
}: Omit<ConditionBuilderProps, 'depth'>) {
  if (!('type' in value)) return null;
  const removeButton = null;
  switch (value.type) {
    case 'has_permission':
      return (
        <LeafRow label={LEAF_LABELS.has_permission}>
          <SimpleSelect
            aria-label="Permission"
            size="sm"
            className="w-64 font-mono"
            value={value.permission}
            options={PERMISSION_KEYS.map((p) => ({ value: p, label: p }))}
            onChange={(next) =>
              next && onChange({ type: 'has_permission', permission: next as PermissionKey })
            }
          />
          {removeButton}
        </LeafRow>
      );
    case 'user_is':
      return (
        <LeafRow label={LEAF_LABELS.user_is}>
          <SimpleSelect
            aria-label="Who"
            size="sm"
            className="w-48"
            value={value.who}
            options={WHO_OPTIONS}
            onChange={(next) =>
              next &&
              onChange({ type: 'user_is', who: next as 'assignee' | 'reporter' | 'project_lead' })
            }
          />
        </LeafRow>
      );
    case 'user_in_role':
      return (
        <LeafRow label={LEAF_LABELS.user_in_role}>
          <SimpleSelect
            aria-label="Role"
            size="sm"
            className="w-56"
            value={value.roleId || undefined}
            placeholder="Choose a role…"
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onChange={(next) => next && onChange({ type: 'user_in_role', roleId: next })}
          />
        </LeafRow>
      );
    case 'user_in_group':
      return (
        <LeafRow label={LEAF_LABELS.user_in_group}>
          <GroupSelect
            id={`${idPrefix}-group`}
            value={value.groupId}
            onChange={(groupId) => onChange({ type: 'user_in_group', groupId })}
          />
        </LeafRow>
      );
    case 'field_matches':
      return (
        <LeafRow label={LEAF_LABELS.field_matches} block>
          <FieldConditionRow
            idPrefix={`${idPrefix}-field`}
            value={{ field: value.field, op: value.op, value: value.value }}
            catalog={catalog}
            allowMe={false}
            onChange={(next) =>
              onChange({
                type: 'field_matches',
                field: next.field,
                op: next.op,
                ...(next.value === undefined ? {} : { value: next.value }),
              })
            }
          />
        </LeafRow>
      );
    default:
      return null;
  }
}

function LeafRow({
  label,
  block,
  children,
}: {
  label: string;
  block?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-md border border-border bg-surface-raised p-2',
        block ? 'space-y-1.5' : 'flex flex-wrap items-center gap-2',
      )}
      data-slot="condition-leaf"
    >
      <span className="font-medium text-fg-muted text-xs">{label}</span>
      {children}
    </div>
  );
}

/** Group select over `GET /groups` (tenant admin); falls back to an id input elsewhere. */
function GroupSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const groups = useGroups({ pageSize: 200 });
  if (groups.error) {
    return (
      <Input
        id={id}
        aria-label="Group id"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Group id"
        className="h-8 w-72 font-mono text-xs"
      />
    );
  }
  return (
    <SimpleSelect
      id={id}
      aria-label="Group"
      size="sm"
      className="w-56"
      value={value || undefined}
      placeholder={groups.isPending ? 'Loading groups…' : 'Choose a group…'}
      options={groups.items.map((g) => ({ value: g.id, label: g.name }))}
      onChange={(next) => next && onChange(next)}
      disabled={groups.isPending}
    />
  );
}
